import { useState, useRef, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * ServicesNav — an editorial dropdown surfacing the firm's practice
 * disciplines. Hairline dividers, italic serif accents, no boxy borders.
 * Keyboard accessible: opens on focus, closes on Esc / blur / outside click.
 */

const COLUMNS = [
  {
    heading: "Core Disciplines",
    accent: "The six chapters.",
    items: [
      { label: "All Services", href: "/practice", desc: "The full continuum, at a glance." },
      { label: "Business Counsel", href: "/practice#business-counsel", desc: "Formation · M&A · Succession · Franchises · Physicians · Nonprofits." },
      { label: "Asset Purchase Agreements", href: "/texas-asset-purchase-agreement-attorney/", desc: "Buy or sell a Texas business. Structure · LOI · Diligence · Definitive · Close.", featured: true },
      { label: "Business Succession Planning", href: "/texas-business-succession-planning-attorney/", desc: "Family transfers · Management buyouts · Buy-sell agreements · Retirement transitions.", featured: true },
      { label: "LLC Formation & Operating Agreements", href: "/texas-llc-formation-attorney/", desc: "Attorney-guided Texas LLCs · Company agreements · Buy-sell coordination.", featured: true },
      { label: "Trademark & Brand Protection", href: "/texas-trademark-attorney/", desc: "USPTO clearance · Filing · Opposition · Enforcement. For founders, creators, athletes.", featured: true },
      { label: "Physician Practice Transitions", href: "/texas-physician-practice-sale-attorney/", desc: "Physician & dental practice sales · MSO transactions · Stark & anti-kickback · Payor credentialing.", featured: true },
      { label: "Intellectual Property", href: "/practice/intellectual-property", desc: "Trademarks · Copyrights · Brand protection · Licensing.", featured: true },
      { label: "Trusts & Estate Planning", href: "/trusts", desc: "Texas trust planning, business succession, asset protection, and fiduciary counsel.", featured: true },
      { label: "Probate & Estate Administration", href: "/practice#probate", desc: "Independent administration · Muniment · Heirship." },
      { label: "Guardianship", href: "/practice#guardianship", desc: "Elder law · Conservatorship · Special-needs." },
      { label: "Asset Protection", href: "/practice#asset-protection", desc: "Layered domestic architecture. Texas LLC forward." },
    ],
  },
  {
    heading: "Trusts & Estate Planning",
    accent: "Structure for today. Stewardship for generations.",
    items: [
      { label: "Trusts Overview", href: "/trusts", desc: "The Texas trust planning hub.", featured: true },
      { label: "Revocable Living Trusts", href: "/trusts/revocable-living-trusts", desc: "Continuity, incapacity, probate avoidance for properly funded assets." },
      { label: "Irrevocable Trusts", href: "/trusts/irrevocable-trusts", desc: "Beneficiary protection, gifting, life insurance, multigenerational." },
      { label: "Special Needs Trusts", href: "/trusts/special-needs-trusts", desc: "Supplemental support with benefits-coordination review." },
      { label: "Asset Protection & Trust Planning", href: "/trusts/asset-protection-and-trust-planning", desc: "Coordinated entity, insurance, and trust architecture." },
      { label: "Business Owner Trust Planning", href: "/trusts/business-owner-trust-planning", desc: "Trust ownership of business interests, buy-sell coordination." },
      { label: "Trust Administration & Fiduciary Counsel", href: "/trusts/trust-administration", desc: "Trustees, beneficiaries, accountings, distributions, disputes." },
      { label: "Advanced, Charitable & Dynasty", href: "/trusts/advanced-and-charitable-trust-planning", desc: "Dynasty · ILITs · GRATs · IDGTs · SLATs · CRTs · CLTs · GST." },
      { label: "Modification, Decanting & Reformation", href: "/trusts/trust-modification-decanting-and-reformation", desc: "Updating an existing Texas trust." },
    ],
  },
  {
    heading: "Bankruptcy & Restructuring",
    accent: "For the moment things stall.",
    items: [
      { label: "Overview", href: "/practice#bankruptcy", desc: "Chapter 7 · 11 · Subchapter V, business and individual." },
      { label: "Subchapter V", href: "/practice/subchapter-v", desc: "Small-business reorganization, streamlined." },
      { label: "Chapter 11 Reorganization", href: "/practice/chapter-11-reorganization", desc: "Middle-market reorganization and DIP." },
      { label: "Chapter 7 — Business", href: "/practice/chapter-7-business", desc: "Orderly liquidation and dissolution." },
      { label: "Chapter 7 — Individual", href: "/practice/chapter-7-individual", desc: "Personal reset with Texas exemptions." },
    ],
  },
  {
    heading: "Private Client",
    accent: "For the quietly consequential.",
    items: [
      {
        label: "Private Client Services",
        href: "/practice/private-client",
        desc: "Standing counsel for Texas families, family offices, and HNW principals. By referral.",
        featured: true,
      },
      {
        label: "Creators & Athletes",
        href: "/practice/creators-and-athletes",
        desc: "Discreet counsel for digital creators, professional athletes, and recording artists.",
      },
    ],
  },
];

// Exported so the mobile menu accordion in Navbar.jsx can render the same
// content without duplicating this data.
export const SERVICES_COLUMNS = COLUMNS;

export default function ServicesNav({ onDark = false, onNavigate }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const location = useLocation();
  const isActive = location.pathname.startsWith("/practice");

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleItemClick = () => {
    setOpen(false);
    if (onNavigate) onNavigate();
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      data-testid="services-nav"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`nav-link inline-flex items-center gap-1.5 ${isActive ? "active" : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="nav-link-services"
      >
        Services
        <ChevronDown
          size={12}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="fixed right-4 md:right-6 lg:right-8 top-[92px] md:top-[100px] z-[60]"
          data-testid="services-dropdown"
        >
          <div
            className="w-[min(56vw,760px)] shadow-[0_24px_60px_-20px_rgba(8,13,27,0.28)] bg-ivory text-ink"
            style={{ backdropFilter: "blur(8px)" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brass/25">
              {COLUMNS.map((col, ci) => (
                <div
                  key={col.heading}
                  className="bg-ivory p-6 md:p-7"
                  data-testid={`services-col-${ci}`}
                >
                  <div className="kicker text-[9px]">{col.heading}</div>
                  <div className="mt-2 font-cormorant italic text-brass text-[15px] leading-snug">
                    {col.accent}
                  </div>
                  <ul className="mt-5 space-y-4">
                    {col.items.map((item) => {
                      const isFeatured = item.featured;
                      const isAnchor = item.href.includes("#");
                      const commonClass = `group block ${isFeatured ? "hairline-top pt-3" : ""}`;
                      const inner = (
                        <>
                          <div className={`font-display text-navy leading-tight ${isFeatured ? "text-[15px]" : "text-[13.5px]"} group-hover:text-oxblood transition-colors`}>
                            {item.label}
                          </div>
                          <div className="mt-0.5 text-[11px] text-slate2 font-light leading-snug">
                            {item.desc}
                          </div>
                        </>
                      );
                      return (
                        <li key={item.label}>
                          {isAnchor ? (
                            <a
                              href={item.href}
                              className={commonClass}
                              onClick={handleItemClick}
                              data-testid={`services-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                              {inner}
                            </a>
                          ) : (
                            <Link
                              to={item.href}
                              className={commonClass}
                              onClick={handleItemClick}
                              data-testid={`services-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                              {inner}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="border-t border-brass/25 px-6 md:px-7 py-3 flex flex-wrap items-center justify-between gap-3 bg-linen/60">
              <div className="text-[9px] uppercase tracking-[0.32em] text-slate2/80">
                Six Disciplines · Seventy-Two Services
              </div>
              <NavLink
                to="/practice"
                onClick={handleItemClick}
                className="text-[10px] uppercase tracking-[0.28em] text-oxblood hover:text-navy transition font-medium"
                data-testid="services-cta-all"
              >
                Browse the Full Continuum →
              </NavLink>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
